import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, MessageSquare, Workflow, Users, Zap, X } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth, API } from '../App';
import axios from 'axios';

const OnboardingWelcomeModal = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkOnboardingStatus();
  }, [user]);

  const checkOnboardingStatus = async () => {
    // Only show for owner/admin, not operators
    if (!user || user.role === 'operator') {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/onboarding/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Show modal if onboarding not completed
      if (!response.data.onboarding_completed) {
        setShow(true);
      }
    } catch (error) {
      // If 403 or error, don't show modal
      console.error('Failed to check onboarding status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartSetup = () => {
    setShow(false);
    navigate('/dashboard/onboarding');
  };

  const handleSkip = () => {
    setShow(false);
    // Store in localStorage to not show again in this session
    localStorage.setItem('flowteras_onboarding_dismissed', 'true');
  };

  if (loading || !show) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200">
        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-emerald-600 to-emerald-800 p-8 text-white relative">
          <button 
            onClick={handleSkip}
            className="absolute top-4 right-4 p-1 rounded-full hover:bg-white/20 transition-colors"
            data-testid="welcome-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
            <Sparkles className="w-8 h-8" />
          </div>
          
          <h1 className="text-2xl font-bold mb-2">
            Welcome to Flowteras, {user?.name?.split(' ')[0]}! 
          </h1>
          <p className="text-emerald-100">
            Let's set up AI automation for <strong>{user?.organization_name}</strong> in under 5 minutes.
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <p className="text-muted-foreground">
            Our smart setup wizard will help you configure:
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
              <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/50 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-4 h-4 text-emerald-600" />
              </div>
              <span className="text-sm font-medium">WhatsApp Inbox</span>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
              <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/50 rounded-lg flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-emerald-600" />
              </div>
              <span className="text-sm font-medium">AI Replies</span>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
              <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/50 rounded-lg flex items-center justify-center">
                <Workflow className="w-4 h-4 text-emerald-600" />
              </div>
              <span className="text-sm font-medium">Workflows</span>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
              <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/50 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 text-emerald-600" />
              </div>
              <span className="text-sm font-medium">Team Routing</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2 pt-2">
            <Button 
              onClick={handleStartSetup}
              className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base"
              data-testid="welcome-modal-start-btn"
            >
              <Zap className="w-5 h-5 mr-2" />
              Start Smart Setup
            </Button>
            
            <Button 
              variant="ghost" 
              onClick={handleSkip}
              className="w-full text-muted-foreground"
              data-testid="welcome-modal-skip-btn"
            >
              Skip for now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWelcomeModal;
